import { z } from "zod";
import {
  emailField,
  confirmEmailField,
  firstNameField,
  lastNameField,
  organizationField,
  optionalMessageField,
} from "@core/schemas/fields";
import { withEmailConfirmation } from "@core/schemas/refinements";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { Dialog } from "@themes/pollinator/ui/Dialog";

export const hireUsSchema = withEmailConfirmation(
  z.object({
    firstName: firstNameField,
    lastName: lastNameField,
    email: emailField,
    confirmEmail: confirmEmailField,
    organization: organizationField,
    message: optionalMessageField,
  })
);

export type HireUsFormData = z.infer<typeof hireUsSchema>;

export type Props = {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Called when the form is submitted with valid data */
  onSubmit: (data: HireUsFormData) => Promise<void>;
};

/**
 * A dialog form for potential clients to request information about hiring.
 * This is a pure presentational component - all business logic (API calls,
 * event tracking, dialog state) should be handled by the parent.
 *
 * @example
 * <MarketingHireUsDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSubmit={async (data) => {
 *     await apiRequest("/api/hire-us", "POST", data);
 *   }}
 * />
 */
export function MarketingHireUsDialog({ isOpen, onClose, onSubmit }: Props) {
  const { form, handleSubmit, handleClose } = useForm({
    schema: hireUsSchema,
    onSubmit,
    onClose,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      organization: "",
      message: "",
    },
  });

  const { isSubmitting, isValid, isSubmitSuccessful } = form.formState;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={isSubmitSuccessful ? "Success!" : "Hire Us"}
      description={
        isSubmitSuccessful
          ? "Thank you for your interest in working with us. We'll be in touch soon."
          : "Get in touch to learn more about our rates and services."
      }
      isWithActions={!isSubmitSuccessful}
      primaryButtonText="Submit"
      secondaryButtonText="Cancel"
      onPrimaryClick={handleSubmit}
      onSecondaryClick={handleClose}
      primaryButtonProps={{
        disabled: isSubmitting,
        visuallyDisabled: !isSubmitting && !isValid,
        type: "submit",
      }}
      secondaryButtonProps={{
        disabled: isSubmitting,
      }}
    >
      {!isSubmitSuccessful && (
        <Form form={form} className="py-4">
          <FormField
            name="firstName"
            label="First Name"
            component="Input"
            placeholder="Enter your first name"
            required
          />
          <FormField
            name="lastName"
            label="Last Name"
            component="Input"
            placeholder="Enter your last name"
            required
          />
          <FormField
            name="email"
            label="Email"
            component="Input"
            placeholder="Enter your email"
            autoComplete="email"
            required
          />
          <FormField
            name="confirmEmail"
            label="Email Confirmation"
            component="Input"
            placeholder="Confirm your email"
            required
          />
          <FormField
            name="organization"
            label="Organization"
            component="Input"
            placeholder="Enter your organization name"
            required
          />
          <FormField
            name="message"
            label="Message"
            component="Textarea"
            placeholder="Your message"
          />
        </Form>
      )}
    </Dialog>
  );
}
